import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import {Router} from '@angular/router';
import {PersonLogin, RoleName} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {OrganizationService} from '../services/organization-service';
import {PersonLoginService} from '../services/person-login-service';
import {SelectionService} from '../services/selection-service';
import {StringUtils} from '../utils/string-utils';
import {Organization} from './organization';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AddProductDialogComponent} from '../configurator/add-product-dialog/add-product-dialog.component';
import {ConfigurationSaveInformation} from '../model/configuration-save-information';
import {EntityKeyUtils} from '../utils/entity-key-utils';
import {DeactivateRepDialogComponent} from '../dialogs/deactivate-rep-dialog/deactivate-rep-dialog.component';
import {RepDeactivation} from '../model/rep-deactivation';
import {DateUtils} from '../utils/date-utils';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'organization-detail',
    templateUrl: './organization-detail.component.html',
    styleUrls: ['./organization-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatProgressSpinner, DatePipe]
})
export class OrganizationDetailComponent implements OnInit, OnChanges {
  readonly organizationId = input.required<string>({ alias: 'organization-id' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly organizationService = inject(OrganizationService);
  private readonly router = inject(Router);
  private readonly selectionService = inject(SelectionService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);


  public personLogin!: PersonLogin;
  readonly loadedOrganization = signal<Organization>(undefined!);
  readonly isNew = signal<boolean>(false);
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly primaryContacts = signal<SelectionItem[]>(undefined!);
  readonly responsibilities = signal<SelectionItem[]>(undefined!);
  readonly applicationEngineers = signal<SelectionItem[]>(undefined!);
  readonly serviceReps = signal<SelectionItem[]>(undefined!);
  readonly controllingOrgs = signal<SelectionItem[]>(undefined!);
  readonly organizationTypes = signal<SelectionItem[]>(undefined!);
  readonly priceLists = signal<SelectionItem[]>(undefined!);
  readonly installTypes = signal<SelectionItem[]>(undefined!);
  readonly paymentTerms = signal<SelectionItem[]>(undefined!);
  readonly deliveryTerms = signal<SelectionItem[]>(undefined!);
  readonly transportationTerms = signal<SelectionItem[]>(undefined!);
  readonly freightTerms = signal<SelectionItem[]>(undefined!);
  readonly regionalTeams = signal<SelectionItem[]>(undefined!);
  readonly orgStatusCodes = signal<SelectionItem[]>(undefined!);
  readonly siteStatusCodes = signal<SelectionItem[]>(undefined!);
  readonly customerStatusCodes = signal<SelectionItem[]>(undefined!);
  readonly channelStatusCodes = signal<SelectionItem[]>(undefined!);
  readonly parentOrgs = signal<SelectionItem[]>(undefined!);
  public isInternalOrRsm!: boolean;
  public isAdminUser!: boolean;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['organizationId'].firstChange) {
      this.initialize();
    }
  }

  private initialize(): void {
    this.isNew.set(false);
    this.loadSelectionLists();
    const idString: string = this.organizationId();
    if (idString && idString.length > 0) {
      if ("new" === idString) {
        this.startNewOrganization();
      } else {
        this.organizationService.getOrganization(idString).subscribe(result => {
          let userOrg = this.personLogin.person.primaryOrgUnitId;
          if (this.personLogin.roles.length === 0 && result.orgUnitId !== userOrg && result.controllingOrgId !== userOrg) {
            this.backToOrganizationMaster();
          } else {
            this.loadedOrganization.set(result);
          }
        });
      }
    } else {
      this.backToOrganizationMaster();
    }
  }

  public showAddresses(): void {
    this.router.navigate(['organization/addresses/' + this.loadedOrganization().orgUnitId]);
  }

  public showPhones(): void {
    this.router.navigate(['organization/phones/' + this.loadedOrganization().orgUnitId]);
  }

  public showEmails(): void {
    this.router.navigate(['organization/emails/' + this.loadedOrganization().orgUnitId]);
  }

  public showCustomerPolicies(): void {
    this.router.navigate(['organization/policies/' + this.loadedOrganization().orgUnitId]);
  }

  private loadSelectionLists(): void {
    this.selectionService.getOrganizationTypes().subscribe(result => this.organizationTypes.set(result));
    this.selectionService.getPriceLists().subscribe(result => this.priceLists.set(result));
    this.selectionService.getRegionalSalesManagers().subscribe(result => {
      this.primaryContacts.set(JSON.parse(JSON.stringify(result)));
      this.responsibilities.set(JSON.parse(JSON.stringify(result)));
    });
    this.selectionService.getApplicationEngineers().subscribe({next: result => this.applicationEngineers.set(result)});
    this.selectionService.getVendorStatusCodes().subscribe(result => this.installTypes.set(result));
    this.selectionService.getPaymentTerms().subscribe(result => this.paymentTerms.set(result));
    this.selectionService.getDeliveryTerms().subscribe(result => this.deliveryTerms.set(result));
    this.selectionService.getCustomerServiceReps().subscribe(result => this.serviceReps.set(result));
    this.selectionService.getControllingOrgs().subscribe(result => this.controllingOrgs.set(result));
    this.selectionService.getTransportationTerms().subscribe(result => this.transportationTerms.set(result));
    this.selectionService.getFreightTerms().subscribe(result => this.freightTerms.set(result));
    this.selectionService.getRegionalTeams().subscribe(result => this.regionalTeams.set(result));
    this.selectionService.getOrgStatusCodes().subscribe(result => this.orgStatusCodes.set(result));
    this.selectionService.getSiteStatusCodes().subscribe(result => this.siteStatusCodes.set(result));
    this.selectionService.getCustomerStatusCodes().subscribe(result => this.customerStatusCodes.set(result));
    this.selectionService.getChannelStatusCodes().subscribe(result => this.channelStatusCodes.set(result));
    this.selectionService.getActiveRepOffices().subscribe(repOffices => {
      this.selectionService.getActiveRegionOrgs().subscribe(regions => {
        const results: SelectionItem[] = [];
        results.push(...repOffices);
        results.push(...regions);
        this.parentOrgs.set(results);
      });
    });
  }

  private startNewOrganization(): void {
    this.organizationService.getOrganization(this.personLogin.person.primaryOrgUnitId).subscribe(primaryOrg => {
      this.loadedOrganization.set(new Organization());
      this.loadedOrganization().active = true;
      this.loadedOrganization().controllingOrgId = primaryOrg.orgUnitId;
      this.loadedOrganization().controllingOrgDescription = primaryOrg.description;
      this.loadedOrganization().smallTextUserField2 = primaryOrg.smallTextUserField2;
      this.loadedOrganization().smallTextUserField3 = primaryOrg.smallTextUserField3;
      this.loadedOrganization().smallTextUserField4 = primaryOrg.smallTextUserField4;
      this.loadedOrganization().mnemonic = primaryOrg.mnemonic;
      this.loadedOrganization().priceListId = primaryOrg.priceListId;
      this.loadedOrganization().priceListDescription = primaryOrg.priceListDescription;
      this.loadedOrganization().installCode = primaryOrg.installCode;
      this.loadedOrganization().installDescription = primaryOrg.installDescription;
      this.loadedOrganization().paymentTermsCode = primaryOrg.paymentTermsCode;
      this.loadedOrganization().paymentTermsDescription = primaryOrg.paymentTermsDescription;
      this.loadedOrganization().deliveryTermsCode = primaryOrg.deliveryTermsCode;
      this.loadedOrganization().deliveryTermsDescription = primaryOrg.deliveryTermsDescription;
      this.loadedOrganization().transportationTermsCode = primaryOrg.transportationTermsCode;
      this.loadedOrganization().transportationTermsDescription = primaryOrg.transportationTermsDescription;
      this.loadedOrganization().freightTermsCode = primaryOrg.freightTermsCode;
      this.loadedOrganization().freightTermsDescription = primaryOrg.freightTermsDescription;
      this.loadedOrganization().classificationCode = primaryOrg.classificationCode;
      this.loadedOrganization().classificationDescription = primaryOrg.classificationDescription;
      this.loadedOrganization().statusCode = primaryOrg.statusCode;
      this.loadedOrganization().statusDescription = primaryOrg.statusDescription;
      this.loadedOrganization().siteStatusCode = primaryOrg.siteStatusCode;
      this.loadedOrganization().siteStatusDescription = primaryOrg.siteStatusDescription;
      this.loadedOrganization().customerStatusCode = primaryOrg.customerStatusCode;
      this.loadedOrganization().customerStatusDescription = primaryOrg.customerStatusDescription;
      this.loadedOrganization().channelStatusCode = primaryOrg.channelStatusCode;
      this.loadedOrganization().channelStatusDescription = primaryOrg.channelStatusDescription;
      this.loadedOrganization().responsibilityId = primaryOrg.responsibilityId;
      this.loadedOrganization().responsibilityDescription = primaryOrg.responsibilityDescription;
      this.loadedOrganization().serviceRepId = primaryOrg.serviceRepId;
      this.loadedOrganization().serviceRepDescription = primaryOrg.serviceRepDescription;
      this.loadedOrganization().typeCode = 'CUST';
      this.loadedOrganization().typeDescription = 'IEC Direct Sale Customer - Commission Only';
      this.loadedOrganization().pbcId = primaryOrg.pbcId;
      this.loadedOrganization().pbcDescription = primaryOrg.pbcDescription;
      this.loadedOrganization().createUser = this.personLogin.person.userLogin;
      this.loadedOrganization().createTimestamp = new Date();
      this.isNew.set(true);
    });
  }

  backToOrganizationMaster(): void {
    this.router.navigate(['organization']);
  }

  public hasRole(roleName: string): boolean {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  public save(): void {
    if (this.loadedOrganization().typeCode !== 'REGN' && !this.loadedOrganization().controllingOrgId) {
      this.errorMessage.set("Please select a Controlling Org");
    } else {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      this.loadedOrganization().updateUser = this.personLogin.person.userLogin;
      this.organizationService.save(this.loadedOrganization()).subscribe(result => {
        this.isNew.set(false);
        this.isBusy.set(false);
        this.loadedOrganization.set(result);
      }, error => this.handleError(error));
    }
  }

  private handleError(error: HttpErrorResponse) {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.personLogin = this.personLoginService.getCachedLogin()!;
      this.isInternalOrRsm = this.personLoginService.isInternalOrRsm();
      this.isAdminUser = this.personLoginService.hasRole(RoleName.IECIT);
      this.initialize();
    }
  }

  openDeactivateRepDialog(): void {
    if (!this.isNew() && this.isAdminUser) {
      const deactivateRepDialog = this.dialog.open(DeactivateRepDialogComponent, {
        height: '480px',
        width: '640px',
        data: {organization: this.loadedOrganization()},
        disableClose: true
      });
      deactivateRepDialog.afterClosed().subscribe({
        next: (deactivation: RepDeactivation) => {
          if (deactivation && deactivation.newRepId && deactivation.newRepId.length > 0) {
            this.loadedOrganization().largeTextUserField1 = deactivation.newRepId;
            this.loadedOrganization().dateUserField1 = deactivation.cancelDate;
            this.isBusy.set(true);
            this.organizationService.deactivate(this.loadedOrganization()).subscribe({
              next: (response: string) => {
                this.organizationService.getOrganization(this.loadedOrganization().orgUnitId).subscribe({
                  next: (result: Organization) => {
                    this.loadedOrganization.set(result);
                    this.isBusy.set(false);
                    this.snackBar.open(response, 'Close', {duration: 5000});
                  },
                  error: (error) => this.handleError(error),
                });
              },
              error: (error) => this.handleError(error),
            });
          }
        },
        error: (error) => this.handleError(error)
      });
    }
  }

}
